<template>
  <div class="admin-container">
    <div class="admin-header">
      <h1 class="admin-title">
        <i class="el-icon-s-management"></i>
        管理后台
      </h1>
    </div>

    <div class="admin-content">
      <el-tabs v-model="activeTab" type="border-card" @tab-click="handleTabClick">
        <el-tab-pane label="文章管理" name="article-audit">
          <div class="tab-content">
            <div class="filter-bar">
              <el-input
                v-model="auditKeyword"
                placeholder="搜索文章标题或作者"
                prefix-icon="el-icon-search"
                clearable
                style="width: 300px"
                @keyup.enter.native="getAuditList"
              ></el-input>
              <el-select v-model="auditStatus" placeholder="审核状态" style="width: 150px" @change="getAuditList">
                <el-option label="全部" value=""></el-option>
                <el-option label="待审核" :value="2"></el-option>
                <el-option label="已发布" :value="1"></el-option>
                <el-option label="禁用" :value="0"></el-option>
              </el-select>
              <CategorySelect 
                v-model="auditCategory" 
                placeholder="选择分类"
                size="small"
                custom-style="width: 200px;"
                @change="getAuditList"
              />
              <el-date-picker
                v-model="auditDateRange"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                value-format="yyyy-MM-dd HH:mm:ss"
                default-time="['00:00:00', '23:59:59']"
                style="width: 240px"
                @change="getAuditList"
              ></el-date-picker>
              <el-button type="primary" @click="getAuditList">查询</el-button>
            </div>

            <el-table :data="auditList" v-loading="auditLoading" style="width: 100%">
              <el-table-column prop="title" label="文章标题" min-width="200" show-overflow-tooltip></el-table-column>
              <el-table-column prop="createUserName" label="作者" width="120"></el-table-column>
              <el-table-column prop="createTime" label="发布时间" width="180"></el-table-column>
              <el-table-column label="状态" width="100">
                <template slot-scope="scope">
                  <el-tag :type="getStatusType(scope.row.state)" size="small">
                    {{ getStatusText(scope.row.state) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="320" fixed="right">
                <template slot-scope="scope">
                  <div style="display: flex; gap: 4px; flex-wrap: wrap;">
                    <el-button size="mini" type="success" @click="approveArticle(scope.row)" :disabled="scope.row.state === 1">
                      通过
                    </el-button>
                    <el-button size="mini" type="danger" @click="rejectArticle(scope.row)" :disabled="scope.row.state === 0">
                      拒绝
                    </el-button>
                    <el-button size="mini" @click="viewArticle(scope.row)">查看</el-button>
                    <el-button size="mini" type="warning" @click="openEditStats(scope.row)" v-if="canEditStats">
                      调数
                    </el-button>
                  </div>
                </template>
              </el-table-column>
            </el-table>

            <el-pagination
              @size-change="handleAuditSizeChange"
              @current-change="handleAuditCurrentChange"
              :current-page="auditPageNum"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="auditPageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="auditTotal"
              style="margin-top: 20px; justify-content: flex-end"
            ></el-pagination>
          </div>
        </el-tab-pane>

        <el-tab-pane label="新增分类" name="add-category">
          <div class="tab-content">
            <div class="category-form-wrapper">
              <el-card class="form-card">
                <div slot="header">
                  <i class="el-icon-folder-add"></i>
                  <span>添加分类</span>
                </div>
                <el-form :model="categoryForm" :rules="categoryRules" ref="categoryForm" label-width="100px">
                  <el-form-item label="父级分类" prop="parentId">
                    <el-cascader
                      v-model="categoryForm.parentId"
                      :options="categoryTree"
                      :props="{ value: 'id', label: 'categoryName', children: 'children', checkStrictly: true, emitPath: false }"
                      placeholder="选择父级分类（可选）"
                      clearable
                      style="width: 100%"
                    ></el-cascader>
                  </el-form-item>
                  <el-form-item label="分类名称" prop="categoryName">
                    <el-input v-model="categoryForm.categoryName" placeholder="请输入分类名称" maxlength="20" show-word-limit></el-input>
                  </el-form-item>
                  <el-form-item label="分类描述" prop="description">
                    <el-input
                      v-model="categoryForm.description"
                      type="textarea"
                      :rows="3"
                      placeholder="请输入分类描述（可选）"
                      maxlength="100"
                      show-word-limit
                    ></el-input>
                  </el-form-item>
                  <el-form-item label="排序" prop="sortOrder">
                    <el-input-number v-model="categoryForm.sortOrder" :min="0" :max="999" style="width: 100%"></el-input-number>
                  </el-form-item>
                  <el-form-item>
                    <el-button type="primary" @click="submitCategory" :loading="categoryLoading">添加分类</el-button>
                    <el-button @click="resetCategoryForm">重置</el-button>
                  </el-form-item>
                </el-form>
              </el-card>

              <el-card class="form-card">
                <div slot="header">
                  <i class="el-icon-s-grid"></i>
                  <span>分类列表</span>
                </div>
                <el-table :data="categoryTree" style="width: 100%" row-key="id" default-expand-all :tree-props="{children: 'children'}">
                  <el-table-column prop="categoryName" label="分类名称" min-width="200"></el-table-column>
                  <el-table-column label="操作" width="200" fixed="right">
                    <template slot-scope="scope">
                      <el-button size="mini" type="primary" @click="editCategory(scope.row)">编辑</el-button>
                      <el-button size="mini" type="danger" @click="deleteCategory(scope.row.id)">删除</el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </el-card>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane label="用户管理" name="user-manage">
          <div class="tab-content">
            <div class="filter-bar">
              <el-input
                v-model="userKeyword"
                placeholder="搜索用户名、手机号或邮箱"
                prefix-icon="el-icon-search"
                clearable
                style="width: 300px"
                @keyup.enter.native="getUserList"
              ></el-input>
              <el-select v-model="userStatus" placeholder="用户状态" style="width: 150px" @change="getUserList">
                <el-option label="全部" value=""></el-option>
                <el-option label="正常" value="active"></el-option>
                <el-option label="禁用" value="banned"></el-option>
              </el-select>
              <el-button type="primary" @click="getUserList">查询</el-button>
            </div>

            <el-table :data="userList" v-loading="userLoading" style="width: 100%">
              <el-table-column prop="userName" label="用户名" width="150"></el-table-column>
              <el-table-column prop="userPhone" label="手机号" width="120"></el-table-column>
              <el-table-column prop="emailAddress" label="邮箱" min-width="180"></el-table-column>
              <el-table-column label="用户等级" width="100">
                <template slot-scope="scope">
                  <el-tag :type="getLevelType(scope.row.userLevel)" size="small">
                    {{ scope.row.userLevel }}级
                  </el-tag>
                </template>
              </el-table-column>
               <el-table-column label="权限等级" width="320">
                 <template slot-scope="scope">
                   <el-tag :type="getRuleLevelType(scope.row.ruleLevelCode)" size="medium">
                     {{ getRuleLevelText(scope.row.ruleLevelCode) }}
                   </el-tag>
                   <el-button 
                     v-if="scope.row.ruleLevelCode !== undefined && scope.row.ruleLevelCode !== null"
                     size="mini" 
                     type="primary"
                     style="margin-left: 8px"
                     @click="openEditLevel(scope.row)"
                   >
                     修改
                   </el-button>
                 </template>
               </el-table-column>
              <el-table-column prop="createTime" label="注册时间" width="180"></el-table-column>
               <el-table-column label="状态" width="100">
                 <template slot-scope="scope">
                   <el-tag :type="scope.row.state === 1 ? 'success' : 'info'" size="small">
                     {{ scope.row.state === 1 ? '正常' : '禁用' }}
                   </el-tag>
                 </template>
               </el-table-column>
                 <el-table-column label="操作" width="380" fixed="right">
                    <template slot-scope="scope">
                      <div style="display: flex; gap: 4px; flex-wrap: wrap;">
                        <el-button 
                          size="mini" 
                          type="info"
                          @click="viewUserHome(scope.row)"
                        >
                          查看主页
                        </el-button>
                        <el-button 
                          size="mini" 
                          type="primary"
                          @click="openSendMessage(scope.row)"
                        >
                          发消息
                        </el-button>
                        <el-button 
                          size="mini" 
                          type="danger"
                          @click="openResetPassword(scope.row)"
                          v-if="canResetPassword"
                        >
                          重置密码
                        </el-button>
                        <el-button 
                          size="mini" 
                          :type="scope.row.state === 1 ? 'warning' : 'success'"
                          @click="toggleUserStatus(scope.row)"
                        >
                          {{ scope.row.state === 1 ? '禁用' : '启用' }}
                        </el-button>
                      </div>
                    </template>
                 </el-table-column>
             </el-table>

            <el-pagination
              @size-change="handleUserSizeChange"
              @current-change="handleUserCurrentChange"
              :current-page="userPageNum"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="userPageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="userTotal"
              style="margin-top: 20px; justify-content: flex-end"
            ></el-pagination>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>

    <el-dialog title="查看文章" :visible.sync="articleDialogVisible" width="800px">
      <div class="article-view">
        <h2>{{ currentArticle.title }}</h2>
        <div class="article-meta">
          <span><i class="el-icon-user"></i> {{ currentArticle.createUserName }}</span>
          <span><i class="el-icon-time"></i> {{ currentArticle.createTime }}</span>
          <span><i class="el-icon-folder"></i> {{ currentArticle.categoryName }}</span>
        </div>
        <div class="article-content" v-html="currentArticle.content || '暂无内容'"></div>
      </div>
      <span slot="footer">
        <el-button @click="articleDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <el-dialog title="设置文章数据" :visible.sync="editStatsDialogVisible" width="500px">
      <el-form :model="statsForm" label-width="100px">
        <el-form-item label="文章标题">
          <span>{{ statsForm.title }}</span>
        </el-form-item>
        <el-form-item label="浏览量" prop="viewCount">
          <el-input-number v-model="statsForm.viewCount" :min="0" :max="999999" style="width: 100%"></el-input-number>
        </el-form-item>
        <el-form-item label="点赞数" prop="praiseCount">
          <el-input-number v-model="statsForm.praiseCount" :min="0" :max="999999" style="width: 100%"></el-input-number>
        </el-form-item>
        <el-form-item label="评论数" prop="commentCount">
          <el-input-number v-model="statsForm.commentCount" :min="0" :max="999999" style="width: 100%"></el-input-number>
        </el-form-item>
        <el-form-item label="热度值" prop="hotValue">
          <el-input-number v-model="statsForm.hotValue" :min="0" :max="999999" style="width: 100%"></el-input-number>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="editStatsDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitEditStats" :loading="statsLoading">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog title="编辑分类" :visible.sync="categoryEditDialogVisible" width="500px">
      <el-form :model="editCategoryForm" :rules="categoryRules" ref="editCategoryForm" label-width="100px">
        <el-form-item label="分类名称" prop="categoryName">
          <el-input v-model="editCategoryForm.categoryName" placeholder="请输入分类名称" maxlength="20" show-word-limit></el-input>
        </el-form-item>
        <el-form-item label="图标" prop="icon">
          <el-input v-model="editCategoryForm.icon" placeholder="请输入图标 class，如 el-icon-folder"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="categoryEditDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitEditCategory" :loading="categoryLoading">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog title="修改权限等级" :visible.sync="editLevelDialogVisible" width="400px">
      <el-form :model="levelForm" label-width="80px">
        <el-form-item label="用户名">
          <span>{{ currentEditingUserName }}</span>
        </el-form-item>
        <el-form-item label="权限等级">
          <el-select v-model="levelForm.ruleLevelCode" placeholder="请选择权限等级" style="width: 100%">
            <el-option label="游客 (1)" :value="1"></el-option>
            <el-option label="用户 (2)" :value="2"></el-option>
            <el-option label="会员 (3)" :value="3"></el-option>
            <el-option label="高级会员 (4)" :value="4"></el-option>
            <el-option label="管理员 (20)" :value="20"></el-option>
            <el-option label="超级管理员 (999)" :value="999"></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="editLevelDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="updateUserRuleLevel" :loading="levelLoading">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog title="发送站消息" :visible.sync="sendMessageDialogVisible" width="500px">
      <el-form :model="messageForm" :rules="messageRules" ref="messageForm" label-width="80px">
        <el-form-item label="接收用户">
          <span>{{ messageForm.receiverName }}</span>
        </el-form-item>
        <el-form-item label="消息标题" prop="title">
          <el-input v-model="messageForm.title" placeholder="请输入消息标题" maxlength="50" show-word-limit></el-input>
        </el-form-item>
        <el-form-item label="消息内容" prop="content">
          <el-input 
            v-model="messageForm.content" 
            type="textarea" 
            :rows="6" 
            placeholder="请输入消息内容" 
            maxlength="500"
            show-word-limit
          ></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="sendMessageDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitSendMessage" :loading="messageLoading">发送</el-button>
      </span>
    </el-dialog>

    <el-dialog title="重置用户密码" :visible.sync="resetPasswordDialogVisible" width="450px">
      <div class="reset-password-content">
        <el-alert
          title="密码重置确认"
          type="warning"
          :closable="false"
          show-icon
        >
          <p>即将重置用户 <strong>{{ resetPasswordForm.userName }}</strong> 的密码</p>
          <p>重置后的默认密码为：<strong style="color: #f56c6c; font-size: 16px;">123456789</strong></p>
          <p style="margin-top: 10px;">请提醒用户登录后及时修改密码！</p>
        </el-alert>
      </div>
      <span slot="footer">
        <el-button @click="resetPasswordDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="submitResetPassword" :loading="resetPasswordLoading">确认重置</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import CategorySelect from '../components/CategorySelect.vue';
export default {
  name: 'Admin',
  components: {
    CategorySelect
  },
  data() {
    return {
      activeTab: 'article-audit',
      auditList: [],
      auditLoading: false,
      auditKeyword: '',
      auditStatus: '',
      auditCategory: 0,
      auditDateRange: [],
      auditPageNum: 1,
      auditPageSize: 10,
      auditTotal: 0,

      categoryForm: {
        parentId: null,
        categoryName: '',
        description: '',
        sortOrder: 0
      },
      categoryRules: {
        categoryName: [{ required: true, message: '请输入分类名称', trigger: 'blur' }]
      },
      categoryLoading: false,
      categoryEditDialogVisible: false,
      editCategoryForm: {
        id: null,
        categoryName: '',
        icon: ''
      },

      userList: [],
      userLoading: false,
      userKeyword: '',
      userStatus: '',
      userPageNum: 1,
      userPageSize: 10,
      userTotal: 0,

      articleDialogVisible: false,
      currentArticle: {},

      editLevelDialogVisible: false,
      currentEditingUserId: null,
      currentEditingUserName: '',
      levelForm: {
        ruleLevelCode: null
      },
      levelLoading: false,

      sendMessageDialogVisible: false,
      messageForm: {
        receiverId: null,
        receiverName: '',
        title: '',
        content: ''
      },
      messageRules: {
        title: [{ required: true, message: '请输入消息标题', trigger: 'blur' }],
        content: [{ required: true, message: '请输入消息内容', trigger: 'blur' }]
      },
      messageLoading: false,

      editStatsDialogVisible: false,
      statsForm: {
        docDigest: '',
        title: '',
        viewCount: 0,
        praiseCount: 0,
        commentCount: 0,
        hotValue: 0
      },
      statsLoading: false,
      canEditStats: false,
      canResetPassword: false,
      resetPasswordDialogVisible: false,
      resetPasswordForm: {
        userId: null,
        userName: ''
      }
    };
  },
  created() {
    this.getAuditList();
    this.getCategoryTree();
    this.getUserList();
    this.checkStatsPermission();
    this.checkResetPasswordPermission();
  },
  methods: {
    handleTabClick(tab) {
      if (tab.name === 'article-audit') {
        this.getAuditList();
      } else if (tab.name === 'add-category') {
        this.getCategoryTree();
      } else if (tab.name === 'user-manage') {
        this.getUserList();
      }
    },

    getStatusType(state) {
      // 后端状态值：1=已发布，0=禁用，2=待审核
      const types = { 1: 'success', 0: 'info', 2: 'warning' };
      return types[state] || 'info';
    },
    getStatusText(state) {
      // 后端状态值：1=已发布，0=禁用，2=待审核
      const texts = { 1: '已发布', 0: '禁用', 2: '待审核' };
      return texts[state] || '未知';
    },
    getLevelType(level) {
      if (level >= 999) return 'danger';
      if (level >= 100) return 'warning';
      if (level >= 10) return 'success';
      return 'info';
    },
    getRuleLevelText(code) {
      const ruleLevels = {
        1: '游客',
        2: '用户',
        3: '会员',
        4: '高级会员',
        20: '管理员',
        998: '超管老婆',
        999: '超级管理员'
      };
      return ruleLevels[code] || '未知';
    },
    getRuleLevelType(code) {
      if (code >= 999) return 'danger';
      if (code >= 20) return 'warning';
      if (code >= 3) return 'success';
      return 'info';
    },

    async getAuditList() {
      this.auditLoading = true;
      try {
        const loginResult = JSON.parse(localStorage.getItem('loginResult') || '{}');
        const data = {
          pageNum: this.auditPageNum,
          pageSize: this.auditPageSize,
          state: this.auditStatus,
          token: loginResult.token || '',
          userId: loginResult.userId || '',
          userName: loginResult.userName || '',
          ip: '',
          source: 'web'
        };
        if (this.auditKeyword) {
          data.title = this.auditKeyword;
          data.createUserName = this.auditKeyword;
        }
        if (this.auditCategory && this.auditCategory !== 0) {
          data.categoryId = this.auditCategory;
          data.includeSubCategories = true;
        }
        if (this.auditDateRange && this.auditDateRange.length === 2) {
          data.startTime = this.auditDateRange[0];
          data.endTime = this.auditDateRange[1];
        }
        const response = await this.$axios({
          method: 'post',
          url: '/doc/queryAuditDocList',
          data: data
        });
        if (response.code === '200' || response.code === 200) {
          this.auditList = response.data.data || [];
          this.auditTotal = response.data.total || 0;
        } else {
          this.$message.error(response.msg || '获取审核列表失败');
        }
      } catch (error) {
        console.error(error);
        this.$message.error('获取审核列表失败');
      } finally {
        this.auditLoading = false;
      }
    },
    handleAuditSizeChange(val) {
      this.auditPageSize = val;
      this.getAuditList();
    },
    handleAuditCurrentChange(val) {
      this.auditPageNum = val;
      this.getAuditList();
    },
    approveArticle(row) {
      this.$confirm('确认通过该文章？', '提示', { type: 'warning' })
        .then(async () => {
          const loginResult = JSON.parse(localStorage.getItem('loginResult') || '{}');
          const response = await this.$axios({
            method: 'post',
            url: '/doc/auditPassDoc',
            data: {
              docDigest: row.docDigest,
              token: loginResult.token || '',
              userId: loginResult.userId || '',
              userName: loginResult.userName || '',
              ip: '',
              source: 'web'
            }
          });
          if (response.code === '200' || response.code === 200) {
            this.$message.success('已通过');
            this.getAuditList();
          } else {
            this.$message.error(response.msg || '操作失败');
          }
        })
        .catch(() => {});
    },
    rejectArticle(row) {
      this.$prompt('请输入拒绝原因', '提示', { type: 'warning' })
        .then(async ({ value }) => {
          const loginResult = JSON.parse(localStorage.getItem('loginResult') || '{}');
          const response = await this.$axios({
            method: 'post',
            url: '/doc/auditFailDoc',
            data: {
              docDigest: row.docDigest,
              token: loginResult.token || '',
              userId: loginResult.userId || '',
              userName: loginResult.userName || '',
              ip: '',
              source: 'web',
              reason: value
            }
          });
          if (response.code === '200' || response.code === 200) {
            this.$message.success('已拒绝');
            this.getAuditList();
          } else {
            this.$message.error(response.msg || '操作失败');
          }
        })
        .catch(() => {});
    },
    viewArticle(row) {
      // 获取文章详情（包含内容）
      const loginResult = JSON.parse(localStorage.getItem('loginResult') || '{}');
      this.$axios({
        method: 'post',
        url: '/doc/getDoc',
        data: {
          docDigest: row.docDigest,
          token: loginResult.token || '',
          userId: loginResult.userId || '',
          userName: loginResult.userName || '',
          ip: '',
          source: 'web'
        }
      }).then(response => {
        if (response.code === '200' || response.code === 200) {
          this.currentArticle = response.data;
          this.articleDialogVisible = true;
        } else {
          this.$message.error(response.msg || '获取文章详情失败');
        }
      }).catch(error => {
        console.error(error);
        this.$message.error('获取文章详情失败');
      });
    },

    async getCategoryTree() {
      try {
        const response = await this.$axios({
          method: 'post',
          url: '/category/categoryTree',
          data: {}
        });
        if (response.code === '200') {
          const rootData = response.data;
          if (rootData && rootData.children) {
            this.categoryTree = rootData.children;
            this.categoryList = this.flattenCategoryTree(rootData.children);
            this.categoryOptions = [{ id: 0, categoryName: '全部', children: this.categoryTree }];
            this.hasSubCategories = this.categoryTree.some(cat => cat.children && cat.children.length > 0);
          } else {
            this.categoryTree = [];
            this.categoryList = [];
            this.categoryOptions = [];
            this.hasSubCategories = false;
          }
        }
      } catch (error) {
        console.error('获取分类树失败:', error);
        this.$message.error('获取分类树失败');
      }
    },
    flattenCategoryTree(tree) {
      let result = [];
      tree.forEach(node => {
        const nodeWithoutChildren = { ...node };
        delete nodeWithoutChildren.children;
        result.push(nodeWithoutChildren);
        if (node.children && node.children.length > 0) {
          result = result.concat(this.flattenCategoryTree(node.children));
        }
      });
      return result;
    },
    submitCategory() {
      this.$refs.categoryForm.validate(async (valid) => {
        if (!valid) return;
        this.categoryLoading = true;
        try {
          const response = await this.$axios({
            method: 'post',
            url: '/category/createCategory',
            data: {
              categoryName: this.categoryForm.categoryName,
              parentId: this.categoryForm.parentId || 0,
              icon: this.categoryForm.description || ''
            }
          });
          if (response.code === '200') {
            this.$message.success('添加成功');
            this.resetCategoryForm();
            this.getCategoryTree();
          } else {
            this.$message.error(response.msg || '添加失败');
          }
        } catch (error) {
          console.error(error);
          this.$message.error('添加失败');
        } finally {
          this.categoryLoading = false;
        }
      });
    },
    resetCategoryForm() {
      this.categoryForm = { parentId: null, categoryName: '', description: '', sortOrder: 0 };
      if (this.$refs.categoryForm) {
        this.$refs.categoryForm.clearValidate();
      }
    },
    editCategory(row) {
      this.editCategoryForm = {
        id: row.id,
        categoryName: row.categoryName,
        icon: row.icon || ''
      };
      this.categoryEditDialogVisible = true;
    },
    submitEditCategory() {
      this.$refs.editCategoryForm.validate(async (valid) => {
        if (!valid) return;
        this.categoryLoading = true;
        try {
          const response = await this.$axios({
            method: 'post',
            url: '/category/updateCategory',
            data: {
              categoryId: this.editCategoryForm.id,
              categoryName: this.editCategoryForm.categoryName,
              icon: this.editCategoryForm.icon
            }
          });
          if (response.code === '200') {
            this.$message.success('修改成功');
            this.categoryEditDialogVisible = false;
            this.getCategoryTree();
          } else {
            this.$message.error(response.msg || '修改失败');
          }
        } catch (error) {
          console.error(error);
          this.$message.error('修改失败');
        } finally {
          this.categoryLoading = false;
        }
      });
    },
    changeCategoryState(id, currentState) {
      const newState = currentState === 0 ? 1 : 0;
      const actionText = newState === 0 ? '启用' : '禁用';
      this.$confirm(`确认${actionText}该分类？`, '提示', { type: 'warning' })
        .then(async () => {
          try {
            const response = await this.$axios({
              method: 'post',
              url: '/category/changeCategoryState',
              data: {
                categoryId: id,
                state: newState
              }
            });
            if (response.code === '200') {
              this.$message.success(`${actionText}成功`);
              this.getCategoryTree();
            } else {
              this.$message.error(response.msg || `${actionText}失败`);
            }
          } catch (error) {
            console.error(error);
            this.$message.error(`${actionText}失败`);
          }
        })
        .catch(() => {});
    },

    async getUserList() {
      this.userLoading = true;
      try {
        const response = await this.$axios({
          method: 'post',
          url: '/user/manage/list',
          data: {
            pageNum: (this.userPageNum - 1) * this.userPageSize,
            pageSize: this.userPageSize,
            keyword: this.userKeyword,
            state: this.userStatus === '' ? null : (this.userStatus === 'normal' ? 0 : 1)
          }
        });
        if (response.code === '200') {
          this.userList = response.data || [];
          this.userTotal = this.userList.length;
        }
      } catch (error) {
        console.error(error);
        this.$message.error('获取用户列表失败');
      } finally {
        this.userLoading = false;
      }
    },
    handleUserSizeChange(val) {
      this.userPageSize = val;
      this.getUserList();
    },
    handleUserCurrentChange(val) {
      this.userPageNum = val;
      this.getUserList();
    },
    toggleUserStatus(row) {
      const newState = row.state === 0 ? 1 : 0;
      const actionText = newState === 0 ? '启用' : '禁用';
      this.$confirm(`确认${actionText}该用户？`, '提示', { type: 'warning' })
        .then(async () => {
          try {
            const response = await this.$axios({
              method: 'post',
              url: '/user/manage/updateState',
              data: {
                userId: row.id,
                state: newState
              }
            });
            if (response.code === '200') {
              this.$message.success(`${actionText}成功`);
              row.state = newState;
              this.getUserList();
            } else {
              this.$message.error(response.msg || `${actionText}失败`);
            }
          } catch (error) {
            console.error(error);
            this.$message.error(`${actionText}失败`);
          }
        })
        .catch(() => {});
    },
    async updateUserRuleLevel() {
      if (!this.levelForm.ruleLevelCode) {
        this.$message.warning('请选择权限等级');
        return;
      }

      try {
        const response = await this.$axios({
          method: 'post',
          url: '/user/manage/updateRuleLevel',
          data: {
            updateUserId: this.currentEditingUserId,
            ruleLevelCode: this.levelForm.ruleLevelCode
          }
        });
        
        if (response.code === '200') {
          this.$message.success('修改成功');
          this.editLevelDialogVisible = false;
          this.getUserList();
        } else {
          this.$message.error(response.msg || '修改失败');
        }
      } catch (error) {
        console.error(error);
        this.$message.error('修改失败');
      }
    },
    openEditLevel(row) {
      console.log('openEditLevel called with:', row);
      this.currentEditingUserId = row.id;
      this.currentEditingUserName = row.userName;
      this.levelForm.ruleLevelCode = row.ruleLevelCode;
      this.editLevelDialogVisible = true;
    },
    openSendMessage(row) {
      this.messageForm.receiverId = row.id;
      this.messageForm.receiverName = row.userName;
      this.messageForm.title = '';
      this.messageForm.content = '';
      this.sendMessageDialogVisible = true;
    },
    async checkStatsPermission() {
      const loginResult = JSON.parse(localStorage.getItem('loginResult') || '{}');
      const userLevelCode = loginResult.userLevelCode || 0;
      console.log('权限检查：userLevelCode =', userLevelCode);
      this.canEditStats = userLevelCode >= 30;
      console.log('canEditStats =', this.canEditStats);
    },
    async checkResetPasswordPermission() {
      const loginResult = JSON.parse(localStorage.getItem('loginResult') || '{}');
      const userLevelCode = loginResult.userLevelCode || 0;
      this.canResetPassword = userLevelCode >= 20;
    },
    openResetPassword(row) {
      this.resetPasswordForm.userId = row.id;
      this.resetPasswordForm.userName = row.userName;
      this.resetPasswordDialogVisible = true;
    },
    async submitResetPassword() {
      this.resetPasswordLoading = true;
      const loginResult = JSON.parse(localStorage.getItem('loginResult') || '{}');
      
      try {
        const response = await this.$axios({
          method: 'post',
          url: '/user/resetPassword',
          data: {
            restUserId: this.resetPasswordForm.userId,
            token: loginResult.token || '',
            userName: loginResult.userName || '',
            ip: '',
            source: 'web'
          }
        });
        
        if (response.code === '200') {
          this.$message.success('密码重置成功，默认密码：123456789');
          this.resetPasswordDialogVisible = false;
        } else {
          this.$message.error(response.msg || '重置失败');
        }
      } catch (error) {
        console.error(error);
        this.$message.error('重置失败');
      } finally {
        this.resetPasswordLoading = false;
      }
    },
    openEditStats(row) {
      this.statsForm.docDigest = row.docDigest;
      this.statsForm.title = row.title;
      this.statsForm.viewCount = row.viewCount || 0;
      this.statsForm.praiseCount = row.praiseCount || 0;
      this.statsForm.commentCount = row.commentCount || 0;
      this.statsForm.hotValue = row.hotValue || 0;
      this.editStatsDialogVisible = true;
    },
    async submitEditStats() {
      this.statsLoading = true;
      const loginResult = JSON.parse(localStorage.getItem('loginResult') || '{}');
      
      try {
        const response = await this.$axios({
          method: 'post',
          url: '/doc/updateDocStats',
          data: {
            docDigest: this.statsForm.docDigest,
            viewCount: this.statsForm.viewCount,
            praiseCount: this.statsForm.praiseCount,
            commentCount: this.statsForm.commentCount,
            hotValue: this.statsForm.hotValue,
            token: loginResult.token || '',
            userId: loginResult.userId || '',
            userName: loginResult.userName || '',
            ip: '',
            source: 'web'
          }
        });
        
        if (response.code === '200') {
          this.$message.success('设置成功');
          this.editStatsDialogVisible = false;
          this.getAuditList();
        } else {
          this.$message.error(response.msg || '设置失败');
        }
      } catch (error) {
        console.error(error);
        this.$message.error('设置失败');
      } finally {
        this.statsLoading = false;
      }
    },
    viewUserHome(row) {
      const loginResult = JSON.parse(localStorage.getItem('loginResult') || '{}');
      if (loginResult.userId && loginResult.userId.toString() === row.id.toString()) {
        this.$router.push({ name: 'UserCenter' });
      } else {
        this.$router.push({ name: 'OtherUserCenter', params: { userId: row.id } });
      }
    },
    async submitSendMessage() {
      this.$refs.messageForm.validate(async (valid) => {
        if (!valid) return;
        
        this.messageLoading = true;
        try {
          const response = await this.$axios({
            method: 'post',
            url: '/message/sendSystem',
            data: {
              receiverId: this.messageForm.receiverId,
              title: this.messageForm.title,
              content: this.messageForm.content
            }
          });
          
          if (response.code === '200') {
            this.$message.success('消息发送成功');
            this.sendMessageDialogVisible = false;
          } else {
            this.$message.error(response.msg || '发送失败');
          }
        } catch (error) {
          console.error(error);
          this.$message.error('发送失败');
        } finally {
          this.messageLoading = false;
        }
      });
    },
    deleteUser(row) {
      this.$confirm('确认删除该用户？此操作不可恢复！', '严重警告', { type: 'error' })
        .then(async () => {
          const response = await this.$axios({
            method: 'post',
            url: '/admin/user/delete',
            data: { userId: row.id }
          });
          if (response.code === '200') {
            this.$message.success('删除成功');
            this.getUserList();
          } else {
            this.$message.error(response.msg || '删除失败');
          }
        })
        .catch(() => {});
    }
  }
};
</script>

<style lang="less" scoped>
.admin-container {
  min-height: calc(100vh - 64px);
  margin-top: 64px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 24px;

  .admin-header {
    max-width: 1400px;
    margin: 0 auto 24px;
    padding: 24px 32px;
    background: rgba(255, 255, 255, 0.98);
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);

    .admin-title {
      margin: 0;
      font-size: 26px;
      font-weight: 700;
      color: #333;
      display: flex;
      align-items: center;
      gap: 14px;

      i {
        color: #667eea;
        font-size: 32px;
      }
    }
  }

  .admin-content {
    max-width: 1400px;
    margin: 0 auto;

    ::v-deep .el-tabs {
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
      border: none;

      .el-tabs__header {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        padding: 0;
        margin: 0;
        border-bottom: none;

        .el-tabs__item {
          color: rgba(255, 255, 255, 0.85);
          border: none;
          font-size: 15px;
          font-weight: 500;
          padding: 2px 36px;
          transition: all 0.3s ease;
          background: transparent;

          &.is-active {
            background: #fff;
            color: #667eea;
            font-weight: 600;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          }

          &:hover {
            color: #fff;
            background: rgba(255, 255, 255, 0.1);
          }
        }
      }

      .el-tabs__content {
        padding: 28px;
        background: #fff;
        min-height: 600px;
      }
    }

    .tab-content {
      .filter-bar {
        display: flex;
        gap: 16px;
        margin-bottom: 24px;
        align-items: center;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 12px;

        ::v-deep .el-input__inner,
        ::v-deep .el-select__input {
          border-radius: 8px;
          border-color: #e0e0e0;
          transition: all 0.3s;

          &:hover {
            border-color: #667eea;
          }

          &:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          }
        }

        ::v-deep .el-button--primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          border-radius: 8px;
          padding: 10px 24px;
          font-weight: 500;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
          transition: all 0.3s;

          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
          }
        }
      }

      .category-form-wrapper {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 24px;

        .form-card {
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
          border: 1px solid #e8e8e8;

          ::v-deep .el-card__header {
            background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
            border-bottom: 2px solid #667eea;
            display: flex;
            align-items: center;
            gap: 12px;
            font-weight: 600;
            color: #333;
            padding: 16px 20px;

            i {
              color: #667eea;
              font-size: 20px;
            }
          }

          ::v-deep .el-card__body {
            padding: 24px;
          }
        }
      }

      ::v-deep .el-table {
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
        border: 1px solid #e8e8e8;

        th {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: #fff;
          font-weight: 600;
          font-size: 14px;
          border: none;
          padding: 14px 12px;
        }

        td {
          padding: 14px 12px;
          border-bottom: 1px solid #f0f0f0;
          font-size: 14px;
          color: #333;
        }

        .el-table__body tr {
          &:hover {
            td {
              background: #f8f9ff;
            }
          }
        }

        .el-table__empty-block {
          padding: 60px 0;
        }
      }

      ::v-deep .el-pagination {
        padding: 20px 0 0;
        justify-content: flex-end;

        .el-pager li {
          border-radius: 6px;
          min-width: 36px;
          height: 36px;
          line-height: 36px;
          font-weight: 500;

          &.active {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: #fff;
          }

          &:hover {
            background: #667eea;
            color: #fff;
          }
        }

        .btn-prev,
        .btn-next {
          border-radius: 6px;
          min-width: 36px;
          height: 36px;
          line-height: 36px;
        }
      }
    }
  }
}

.article-view {
  h2 {
    margin: 0 0 16px;
    font-size: 22px;
    font-weight: 600;
    color: #333;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .article-meta {
    display: flex;
    gap: 24px;
    color: #999;
    font-size: 14px;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid #f0f0f0;

    span {
      display: flex;
      align-items: center;
      gap: 6px;

      i {
        color: #667eea;
      }
    }
  }

  .article-content {
    line-height: 1.8;
    color: #333;
    max-height: 400px;
    overflow-y: auto;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 8px;
  }

  .reset-password-content {
    .el-alert {
      p {
        margin: 8px 0;
        line-height: 1.6;
      }
    }
  }
}

::v-deep .el-button--success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);

  &:hover {
    box-shadow: 0 4px 16px rgba(16, 185, 129, 0.4);
  }
}

::v-deep .el-button--danger {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: none;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);

  &:hover {
    box-shadow: 0 4px 16px rgba(239, 68, 68, 0.4);
  }
}

::v-deep .el-button--small {
  padding: 6px 12px;
  font-size: 12px;
  border-radius: 4px;
}

::v-deep .el-tag {
  border-radius: 6px;
  padding: 4px 10px;
  font-weight: 500;
  border: none;

  &.el-tag--success {
    background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
    color: #065f46;
  }

  &.el-tag--warning {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    color: #92400e;
  }

  &.el-tag--danger {
    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
    color: #991b1b;
  }
}

::v-deep .el-switch__core {
  border-radius: 12px;
  height: 24px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

@media (max-width: 1200px) {
  .admin-container {
    padding: 16px;
  }

  .category-form-wrapper {
    grid-template-columns: 1fr !important;
  }

  ::v-deep .el-tabs__item {
    padding: 14px 20px !important;
    font-size: 14px !important;
  }
}
</style>
